import { Card, Box, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { RootState } from '../../redux/store';
import { addToCartAsync, removeFromCartAsync, removeOneFromCartAsync } from '../../redux/reducers/shoppingCartReducer';
import { ServiceItem } from 'orderme-api-integration-client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { QuantityControl } from './quantityControl';
import { CartProductPrice } from './cartProductPrice';
import { ImageLoader } from '../imgLoader/imgLoader';
import { apiKey, imagesUri } from '../../helpers/clientConfigs';
import ClothesIcon from '../../logo/laundry-clothes.png';
import SewingIcon from '../../logo/sewing.png';
import { isSewingService } from '../../helpers/servicesHelper';
import CancelIcon from '@mui/icons-material/Cancel';

interface OneColumnProductCardProps {
  item: ServiceItem;
  count: number;
  removable: boolean;
  isSmallFont?: boolean;
}
export function OneColumnProductCard({ item, count, removable, isSmallFont }: OneColumnProductCardProps) {
  const dispatch = useAppDispatch();
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);
  const isSewing = isSewingService(item);

  const addToCart = () => {
    dispatch(addToCartAsync(item.id));
  };

  const removeOneFromCart = () => {
    dispatch(removeOneFromCartAsync(item.id));
  };

  const removeAllFromCart = () => {
    dispatch(removeFromCartAsync(item.id));
  };

  return (
    <Card
      sx={{
        borderRadius: '10px',
        height: 'auto',
        width: '100%',
        backgroundColor: 'white',
        py: '5px',
      }}
    >
      <Grid
        container
        sx={{ width: '100%', mx: '5px', pl: '5px', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box
          sx={{
            width: isSmallFont ? '50px' : '75px',
            flexShrink: 0,
            textAlign: 'center',
            pr: '10px',
          }}
        >
          {item.logoId ? (
            <ImageLoader
              src={`${imagesUri}/${item.logoId}?orderme-api-key=${apiKey}&height=500`}
              sx={{
                height: isSmallFont ? '50px' : '75px',
                maxWidth: isSmallFont ? '50px' : '75px',
                objectFit: 'cover',
              }}
              alt="SalesPoint"
            />
          ) : (
            <ImageLoader
              src={isSewing ? SewingIcon : ClothesIcon}
              sx={{
                maxHeight: isSmallFont ? '40px' : '100px',
                maxWidth: isSmallFont ? '50px' : '50px',
                py: '10px',
                objectFit: 'cover',
              }}
              alt={isSewing ? 'sewing' : 'clothes'}
            />
          )}
        </Box>
        <Grid size="grow" sx={{ width: '100%', fontSize: isSmallFont ? '14px' : '16px' }}>
          <Grid container sx={{ justifyContent: 'space-between', alignContent: 'flex-start' }} direction="row">
            <Grid size="grow">{item.name}</Grid>
            <Grid size="auto">
              {removable && (
                <IconButton
                  disabled={updatingCart}
                  sx={{
                    color: 'error.main',
                    mt: '-5px',
                    py: '0px',
                    px: '5px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <CancelIcon
                    onClick={removeAllFromCart}
                    sx={{
                      height: isSmallFont ? '25px' : '30px',
                      width: isSmallFont ? '25px' : '30px',
                    }}
                  />
                </IconButton>
              )}
            </Grid>
          </Grid>
          <Grid container sx={{ justifyContent: 'center', pr: '40px', width: '100%' }}>
            <Grid sx={{ display: 'flex', maxWidth: '200px', width: '100%' }}>
              <QuantityControl
                count={count}
                onAdd={addToCart}
                onRemove={removeOneFromCart}
                isEditable={removable}
                fontSize={isSmallFont ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <CartProductPrice item={item} serviceCount={count} isSmallFont={isSmallFont} />
        </Grid>
      </Grid>
    </Card>
  );
}
